import {reactive, computed, watch} from 'vue';
import {PLATFORM_FEE_PER_KM_FLOOR} from '../globals.js';

/**
 * Whether a numeric form field holds a usable non-negative number.
 *
 * A cleared `<input type="number">` yields '' rather than null, and `'' >= 0` coerces to true — so a
 * plain comparison marks an emptied money field as valid and lets it be saved as nothing at all.
 */
const isNonNegativeNumber = (value) =>
    value !== '' && value !== null && value !== undefined &&
    !Number.isNaN(Number(value)) && Number(value) >= 0;

export default function FareConfiguration(data = {}) {
    const state = reactive({
        id: data.id || '',
        currency: data.currency || 'CAD',
        isActive: data.isActive !== undefined ? data.isActive : true,
        serviceProviderId: data.serviceProviderId || '',
        freeStopWaitingMinutes: data.freeStopWaitingMinutes || 0,
        stopPerMinuteCharge: data.stopPerMinuteCharge || 0,
        perStopCharge: data.perStopCharge || 0,
        platformFee: data.platformFee !== undefined ? data.platformFee : 0.69,
        platformFeePerKm: data.platformFeePerKm !== undefined && data.platformFeePerKm !== null ? data.platformFeePerKm : 0,
        platformFeeMinimum: data.platformFeeMinimum !== undefined && data.platformFeeMinimum !== null ? data.platformFeeMinimum : 0,
        platformFeeMaximum: data.platformFeeMaximum !== undefined ? data.platformFeeMaximum : null,
        scheduledRideFee: data.scheduledRideFee !== undefined ? data.scheduledRideFee : 0,
        perMinuteFare: data.perMinuteFare !== undefined ? data.perMinuteFare : 0,
        minimumFare: data.minimumFare !== undefined ? data.minimumFare : 0,
        // Null is the corrected behaviour, not the old one: a row written before the column existed
        // reads back null, and the server reads null the same way.
        minimumAppliesToFareOnly: data.minimumAppliesToFareOnly !== undefined && data.minimumAppliesToFareOnly !== null
            ? data.minimumAppliesToFareOnly : true,
        perAdditionalPassengerCharge: data.perAdditionalPassengerCharge !== undefined ? data.perAdditionalPassengerCharge : 0,
        additionalPassengerMaxCharge: data.additionalPassengerMaxCharge !== undefined ? data.additionalPassengerMaxCharge : 0,
        additionalPassengerFreeUnderAge: data.additionalPassengerFreeUnderAge !== undefined ? data.additionalPassengerFreeUnderAge : null,
        vehicleBaseFareConfigs: data.vehicleBaseFareConfigs || [],
        distanceFareConfigs: data.distanceFareConfigs || [],
        geoZoneFareConfigs: data.geoZoneFareConfigs || []
    });

    const validations = {
        isCurrencyValid: computed(() => !!state.currency.trim()),
        isVehicleBaseFareConfigValid: computed(() => {
            if (state.vehicleBaseFareConfigs.length === 0) return true;
            return state.vehicleBaseFareConfigs.every(pricing =>
                pricing.vehicleType &&
                pricing.baseFare >= 0
            );
        }),
        isDistanceFareConfigValid: computed(() => {
            if (state.distanceFareConfigs.length === 0) return true;
            return state.distanceFareConfigs.every(range => {
                const basicValid = range.startDistanceMeters >= 0 && range.rateAmount >= 0;
                if (range.isOpenEnded) return basicValid;
                return basicValid && range.endDistanceMeters > range.startDistanceMeters;
            });
        }),
        areGeoZoneFareConfigValid: computed(() => {
            if (state.geoZoneFareConfigs.length === 0) return true;
            return state.geoZoneFareConfigs.every(zone =>
                zone.geoZone &&
                zone.applicationType
            );
        }),
        isFreeStopWaitingMinutesValid: computed(() => state.freeStopWaitingMinutes >= 0),
        isStopPerMinuteChargeValid: computed(() => state.stopPerMinuteCharge >= 0),
        isPerStopChargeValid: computed(() => state.perStopCharge >= 0),
        isPlatformFeeValid: computed(() => state.platformFee >= 0),
        // Cleared fields are invalid rather than silently zero: emptying the rate switches the
        // distance-scaled fee off entirely, which is too consequential to happen by accident.
        isPlatformFeePerKmValid: computed(() => isNonNegativeNumber(state.platformFeePerKm)),
        isPlatformFeeMinimumValid: computed(() => isNonNegativeNumber(state.platformFeeMinimum)),
        isPlatformFeeMaximumValid: computed(() => {
            // An empty maximum is the one legitimate blank: it means uncapped.
            if (state.platformFeeMaximum === null || state.platformFeeMaximum === ''
                || state.platformFeeMaximum === undefined) return true;
            if (Number.isNaN(Number(state.platformFeeMaximum))) return false;
            if (Number(state.platformFeeMaximum) < 0) return false;
            // A maximum of 0 means uncapped, so only a positive cap is compared to the minimum.
            if (Number(state.platformFeeMaximum) === 0) return true;
            return Number(state.platformFeeMaximum) >= Number(state.platformFeeMinimum || 0);
        }),
        // Advisory only: deliberately excluded from isValid() so a below-floor rate still saves.
        isPlatformFeePerKmBelowFloor: computed(() =>
            Number(state.platformFeePerKm) > 0 &&
            Number(state.platformFeePerKm) < PLATFORM_FEE_PER_KM_FLOOR
        ),
        isScheduledRideFeeValid: computed(() => state.scheduledRideFee >= 0),
        isPerMinuteFareValid: computed(() => state.perMinuteFare >= 0),
        isMinimumFareValid: computed(() => state.minimumFare >= 0),
        isPerAdditionalPassengerChargeValid: computed(() => state.perAdditionalPassengerCharge >= 0),
        isAdditionalPassengerMaxChargeValid: computed(() => state.additionalPassengerMaxCharge >= 0),
        isAdditionalPassengerFreeUnderAgeValid: computed(() =>
            state.additionalPassengerFreeUnderAge === null ||
            state.additionalPassengerFreeUnderAge === '' ||
            (Number.isInteger(Number(state.additionalPassengerFreeUnderAge)) &&
             Number(state.additionalPassengerFreeUnderAge) >= 0)
        ),
    };

    const validateCurrency = () => {
        if (validations.isCurrencyValid.value) {
            $('#fare-config-currency').removeClass('is-invalid');
            $('#fare-config-currency-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-currency').addClass('is-invalid');
            $('#fare-config-currency-error').removeClass('d-none');
            return false;
        }
    };

    const validateFreeStopWaitingMinutes = () => {
        if (validations.isFreeStopWaitingMinutesValid.value) {
            $('#fare-config-free-waiting-minutes').removeClass('is-invalid');
            $('#fare-config-free-waiting-minutes-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-free-waiting-minutes').addClass('is-invalid');
            $('#fare-config-free-waiting-minutes-error').removeClass('d-none');
            return false;
        }
    };

    const validateStopPerMinuteCharge = () => {
        if (validations.isStopPerMinuteChargeValid.value) {
            $('#fare-config-per-minute-charge').removeClass('is-invalid');
            $('#fare-config-per-minute-charge-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-per-minute-charge').addClass('is-invalid');
            $('#fare-config-per-minute-charge-error').removeClass('d-none');
            return false;
        }
    };

    const validatePerStopCharge = () => {
        if (validations.isPerStopChargeValid.value) {
            $('#fare-config-per-stop-charge').removeClass('is-invalid');
            $('#fare-config-per-stop-charge-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-per-stop-charge').addClass('is-invalid');
            $('#fare-config-per-stop-charge-error').removeClass('d-none');
            return false;
        }
    };

    const validatePlatformFee = () => {
        if (validations.isPlatformFeeValid.value) {
            $('#fare-config-platform-fee').removeClass('is-invalid');
            $('#fare-config-platform-fee-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-platform-fee').addClass('is-invalid');
            $('#fare-config-platform-fee-error').removeClass('d-none');
            return false;
        }
    };

    /**
     * Whether the configured per-km platform fee sits below the policy floor.
     *
     * Exposed as a function rather than only as the computed above because consumers hold this model
     * inside a `ref()`, and a reactive proxy silently unwraps a nested computed — so reading
     * `validations.isPlatformFeePerKmBelowFloor.value` through the proxy yields undefined.
     */
    const isPlatformFeePerKmBelowFloor = () => validations.isPlatformFeePerKmBelowFloor.value;

    const validatePlatformFeePerKm = () => {
        if (validations.isPlatformFeePerKmValid.value) {
            $('#fare-config-platform-fee-per-km').removeClass('is-invalid');
            $('#fare-config-platform-fee-per-km-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-platform-fee-per-km').addClass('is-invalid');
            $('#fare-config-platform-fee-per-km-error').removeClass('d-none');
            return false;
        }
    };

    const validatePlatformFeeMinimum = () => {
        if (validations.isPlatformFeeMinimumValid.value) {
            $('#fare-config-platform-fee-minimum').removeClass('is-invalid');
            $('#fare-config-platform-fee-minimum-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-platform-fee-minimum').addClass('is-invalid');
            $('#fare-config-platform-fee-minimum-error').removeClass('d-none');
            return false;
        }
    };

    const validatePlatformFeeMaximum = () => {
        if (validations.isPlatformFeeMaximumValid.value) {
            $('#fare-config-platform-fee-maximum').removeClass('is-invalid');
            $('#fare-config-platform-fee-maximum-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-platform-fee-maximum').addClass('is-invalid');
            $('#fare-config-platform-fee-maximum-error').removeClass('d-none');
            return false;
        }
    };

    const validateScheduledRideFee = () => {
        if (validations.isScheduledRideFeeValid.value) {
            $('#fare-config-scheduled-ride-fee').removeClass('is-invalid');
            $('#fare-config-scheduled-ride-fee-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-scheduled-ride-fee').addClass('is-invalid');
            $('#fare-config-scheduled-ride-fee-error').removeClass('d-none');
            return false;
        }
    };

    const validatePerMinuteFare = () => {
        if (validations.isPerMinuteFareValid.value) {
            $('#fare-config-per-minute-fare').removeClass('is-invalid');
            $('#fare-config-per-minute-fare-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-per-minute-fare').addClass('is-invalid');
            $('#fare-config-per-minute-fare-error').removeClass('d-none');
            return false;
        }
    };

    const validateMinimumFare = () => {
        if (validations.isMinimumFareValid.value) {
            $('#fare-config-minimum-fare').removeClass('is-invalid');
            $('#fare-config-minimum-fare-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-minimum-fare').addClass('is-invalid');
            $('#fare-config-minimum-fare-error').removeClass('d-none');
            return false;
        }
    };

    const validatePerAdditionalPassengerCharge = () => {
        if (validations.isPerAdditionalPassengerChargeValid.value) {
            $('#fare-config-per-additional-passenger-charge').removeClass('is-invalid');
            $('#fare-config-per-additional-passenger-charge-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-per-additional-passenger-charge').addClass('is-invalid');
            $('#fare-config-per-additional-passenger-charge-error').removeClass('d-none');
            return false;
        }
    };

    const validateAdditionalPassengerMaxCharge = () => {
        if (validations.isAdditionalPassengerMaxChargeValid.value) {
            $('#fare-config-additional-passenger-max-charge').removeClass('is-invalid');
            $('#fare-config-additional-passenger-max-charge-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-additional-passenger-max-charge').addClass('is-invalid');
            $('#fare-config-additional-passenger-max-charge-error').removeClass('d-none');
            return false;
        }
    };

    const validateAdditionalPassengerFreeUnderAge = () => {
        if (validations.isAdditionalPassengerFreeUnderAgeValid.value) {
            $('#fare-config-additional-passenger-free-under-age').removeClass('is-invalid');
            $('#fare-config-additional-passenger-free-under-age-error').addClass('d-none');
            return true;
        } else {
            $('#fare-config-additional-passenger-free-under-age').addClass('is-invalid');
            $('#fare-config-additional-passenger-free-under-age-error').removeClass('d-none');
            return false;
        }
    };

    const validateVehicleBaseFareConfig = (basePrice) => {
        if (!basePrice.vehicleType) {
            return { isValid: false, message: 'Please select a vehicle type.' };
        }
        if (basePrice.baseFare < 0 || basePrice.minimumFare < 0 || basePrice.maximumFare < 0) {
            return { isValid: false, message: 'Fare values cannot be negative.' };
        }
        if (parseFloat(basePrice.minimumFare) > parseFloat(basePrice.maximumFare) &&
            parseFloat(basePrice.maximumFare) > 0) {
            return { isValid: false, message: 'Minimum fare cannot be greater than maximum fare.' };
        }
        return { isValid: true };
    };

    const checkDuplicateVehicleType = (vehicleType, currentIndex = -1) => {
        const existingIndex = state.vehicleBaseFareConfigs.findIndex(
            (pricing, index) => index !== currentIndex && pricing.vehicleType === vehicleType
        );
        if (existingIndex !== -1) {
            return { isDuplicate: true, message: `A base price for ${vehicleType} already exists.` };
        }
        return { isDuplicate: false };
    };

    const validateDistancePricing = (distancePricing) => {
        if (!distancePricing.rateType) {
            return { isValid: false, message: 'Please select a rate type.' };
        }
        if (distancePricing.startDistanceMeters < 0 ||
            distancePricing.endDistanceMeters < 0 ||
            distancePricing.rateAmount < 0) {
            return { isValid: false, message: 'Values cannot be negative.' };
        }
        if (!distancePricing.isOpenEnded) {
            if (parseInt(distancePricing.startDistanceMeters) >= parseInt(distancePricing.endDistanceMeters)) {
                return { isValid: false, message: 'Start distance must be less than end distance.' };
            }
        }
        if (distancePricing.isStartingRange && distancePricing.startDistanceMeters !== 0) {
            return { isValid: false, message: 'A starting range must start at 0 meters.' };
        }
        return { isValid: true };
    };

    const checkOverlappingDistanceFareConfig = (distancePricing, currentIndex = -1) => {
        if (distancePricing.isStartingRange) {
            const existingStartingRangeIndex = state.distanceFareConfigs.findIndex(
                (range, index) => index !== currentIndex && range.isStartingRange
            );
            if (existingStartingRangeIndex !== -1) {
                return { isOverlapping: true, message: 'A starting range already exists. Only one starting range is allowed.' };
            }
        }
        if (distancePricing.isOpenEnded) {
            const existingOpenEndedIndex = state.distanceFareConfigs.findIndex(
                (range, index) => index !== currentIndex && range.isOpenEnded
            );
            if (existingOpenEndedIndex !== -1) {
                return { isOverlapping: true, message: 'An open-ended range already exists. Only one open-ended range is allowed.' };
            }
            const overlappingStartIndex = state.distanceFareConfigs.findIndex(
                (range, index) =>
                    index !== currentIndex &&
                    distancePricing.startDistanceMeters >= range.startDistanceMeters &&
                    distancePricing.startDistanceMeters < range.endDistanceMeters
            );
            if (overlappingStartIndex !== -1) {
                return { isOverlapping: true, message: 'The starting point of this open-ended range overlaps with an existing range.' };
            }
        } else {
            const existingIndex = state.distanceFareConfigs.findIndex(
                (range, index) =>
                    index !== currentIndex &&
                    !range.isOpenEnded &&
                    ((distancePricing.startDistanceMeters >= range.startDistanceMeters &&
                      distancePricing.startDistanceMeters < range.endDistanceMeters) ||
                     (distancePricing.endDistanceMeters > range.startDistanceMeters &&
                      distancePricing.endDistanceMeters <= range.endDistanceMeters) ||
                     (distancePricing.startDistanceMeters <= range.startDistanceMeters &&
                      distancePricing.endDistanceMeters >= range.endDistanceMeters))
            );
            if (existingIndex !== -1) {
                return { isOverlapping: true, message: `A distance range overlapping with ${distancePricing.startDistanceMeters}-${distancePricing.endDistanceMeters} meters already exists.` };
            }
            const openEndedIndex = state.distanceFareConfigs.findIndex(
                (range, index) =>
                    index !== currentIndex &&
                    range.isOpenEnded &&
                    distancePricing.startDistanceMeters >= range.startDistanceMeters
            );
            if (openEndedIndex !== -1) {
                return { isOverlapping: true, message: 'This range overlaps with an existing open-ended range.' };
            }
        }
        return { isOverlapping: false };
    };

    const validateGeoZone = (geoZone) => {
        if (!geoZone.geoZone) {
            return { isValid: false, message: 'Please select a geo zone.' };
        }
        if (!geoZone.applicationType) {
            return { isValid: false, message: 'Please select an application type.' };
        }
        if (!geoZone.zoneRateType) {
            return { isValid: false, message: 'Please select a zone rate type.' };
        }
        if (geoZone.zoneRateType === 'SURCHARGE' && (geoZone.surcharge === null || geoZone.surcharge < 0)) {
            return { isValid: false, message: 'Surcharge cannot be negative.' };
        }
        if (geoZone.zoneRateType === 'PERCENTAGE' && (geoZone.percentage === null || geoZone.percentage < 0)) {
            return { isValid: false, message: 'Percentage cannot be negative.' };
        }
        if (geoZone.zoneRateType === 'FLAT_FARE' && (geoZone.flatFare === null || geoZone.flatFare < 0)) {
            return { isValid: false, message: 'Flat fare cannot be negative.' };
        }
        if (geoZone.zoneRateType === 'PER_KM' && (geoZone.zonePerKmRate === null || geoZone.zonePerKmRate < 0)) {
            return { isValid: false, message: 'Per-km rate cannot be negative.' };
        }
        if (geoZone.fareMaximum !== null && geoZone.fareMaximum !== '' && geoZone.fareMaximum < 0) {
            return { isValid: false, message: 'Fare maximum cannot be negative.' };
        }
        if (geoZone.fareMaximumUpToMeters !== null && geoZone.fareMaximumUpToMeters !== '' && geoZone.fareMaximumUpToMeters < 0) {
            return { isValid: false, message: 'Fare maximum distance bound cannot be negative.' };
        }
        if (geoZone.fareMinimum !== null && geoZone.fareMinimum !== '' && geoZone.fareMinimum < 0) {
            return { isValid: false, message: 'Fare minimum cannot be negative.' };
        }
        if (geoZone.fareMinimumUpToMeters !== null && geoZone.fareMinimumUpToMeters !== '' && geoZone.fareMinimumUpToMeters < 0) {
            return { isValid: false, message: 'Fare minimum distance bound cannot be negative.' };
        }
        // The Asher-side fee is subtracted from the total before the driver is paid, so a negative one
        // would pay the driver out of the platform's margin rather than out of the fare.
        if (geoZone.zonePlatformFee !== null && geoZone.zonePlatformFee !== '' && geoZone.zonePlatformFee < 0) {
            return { isValid: false, message: 'Zone platform fee cannot be negative.' };
        }
        if (geoZone.zonePlatformFeePerKm !== null && geoZone.zonePlatformFeePerKm !== '' && geoZone.zonePlatformFeePerKm < 0) {
            return { isValid: false, message: 'Zone platform fee per km cannot be negative.' };
        }
        return { isValid: true };
    };

    const checkDuplicateGeoZone = (geoZone, currentIndex = -1) => {
        const existingIndex = state.geoZoneFareConfigs.findIndex(
            (zone, index) =>
                index !== currentIndex &&
                zone.geoZone && geoZone.geoZone &&
                zone.geoZone.id === geoZone.geoZone.id &&
                zone.applicationType === geoZone.applicationType
        );
        if (existingIndex !== -1) {
            return { isDuplicate: true, message: 'A geo zone pricing for this zone and application type already exists.' };
        }
        return { isDuplicate: false };
    };

    watch(() => state.currency, validateCurrency);
    watch(() => state.freeStopWaitingMinutes, validateFreeStopWaitingMinutes);
    watch(() => state.stopPerMinuteCharge, validateStopPerMinuteCharge);
    watch(() => state.perStopCharge, validatePerStopCharge);
    watch(() => state.platformFee, validatePlatformFee);
    watch(() => state.platformFeePerKm, validatePlatformFeePerKm);
    watch(() => state.platformFeeMinimum, () => {
        validatePlatformFeeMinimum();
        validatePlatformFeeMaximum();
    });
    watch(() => state.platformFeeMaximum, validatePlatformFeeMaximum);
    watch(() => state.scheduledRideFee, validateScheduledRideFee);
    watch(() => state.perMinuteFare, validatePerMinuteFare);
    watch(() => state.minimumFare, validateMinimumFare);
    watch(() => state.perAdditionalPassengerCharge, validatePerAdditionalPassengerCharge);
    watch(() => state.additionalPassengerMaxCharge, validateAdditionalPassengerMaxCharge);
    watch(() => state.additionalPassengerFreeUnderAge, validateAdditionalPassengerFreeUnderAge);

    const isValid = () => {
        let isCurrencyValid = validateCurrency();
        let isFreeStopWaitingMinutesValid = validateFreeStopWaitingMinutes();
        let isStopPerMinuteChargeValid = validateStopPerMinuteCharge();
        let isPerStopChargeValid = validatePerStopCharge();
        let isPlatformFeeValid = validatePlatformFee();
        let isPlatformFeePerKmValid = validatePlatformFeePerKm();
        let isPlatformFeeMinimumValid = validatePlatformFeeMinimum();
        let isPlatformFeeMaximumValid = validatePlatformFeeMaximum();
        let isScheduledRideFeeValid = validateScheduledRideFee();
        let isPerMinuteFareValid = validatePerMinuteFare();
        let isMinimumFareValid = validateMinimumFare();
        let isPerAdditionalPassengerChargeValid = validatePerAdditionalPassengerCharge();
        let isAdditionalPassengerMaxChargeValid = validateAdditionalPassengerMaxCharge();
        let isAdditionalPassengerFreeUnderAgeValid = validateAdditionalPassengerFreeUnderAge();
        let isVehicleBaseFareConfigValid = validations.isVehicleBaseFareConfigValid.value;
        let isDistanceFareConfigValid = validations.isDistanceFareConfigValid.value;
        let areGeoZoneFareConfigValid = validations.areGeoZoneFareConfigValid.value;

        return isCurrencyValid &&
               isFreeStopWaitingMinutesValid &&
               isStopPerMinuteChargeValid &&
               isPerStopChargeValid &&
               isPlatformFeeValid &&
               isPlatformFeePerKmValid &&
               isPlatformFeeMinimumValid &&
               isPlatformFeeMaximumValid &&
               isScheduledRideFeeValid &&
               isPerMinuteFareValid &&
               isMinimumFareValid &&
               isPerAdditionalPassengerChargeValid &&
               isAdditionalPassengerMaxChargeValid &&
               isAdditionalPassengerFreeUnderAgeValid &&
               isVehicleBaseFareConfigValid &&
               isDistanceFareConfigValid &&
               areGeoZoneFareConfigValid;
    };

    const initializeValidation = () => {
        if (state.currency) validateCurrency();
        if (state.freeStopWaitingMinutes !== undefined) validateFreeStopWaitingMinutes();
        if (state.stopPerMinuteCharge !== undefined) validateStopPerMinuteCharge();
        if (state.perStopCharge !== undefined) validatePerStopCharge();
        if (state.platformFee !== undefined) validatePlatformFee();
        if (state.platformFeePerKm !== undefined) validatePlatformFeePerKm();
        if (state.platformFeeMinimum !== undefined) validatePlatformFeeMinimum();
        if (state.platformFeeMaximum !== undefined) validatePlatformFeeMaximum();
        if (state.scheduledRideFee !== undefined) validateScheduledRideFee();
        if (state.perMinuteFare !== undefined) validatePerMinuteFare();
        if (state.minimumFare !== undefined) validateMinimumFare();
        if (state.perAdditionalPassengerCharge !== undefined) validatePerAdditionalPassengerCharge();
        if (state.additionalPassengerMaxCharge !== undefined) validateAdditionalPassengerMaxCharge();
        if (state.additionalPassengerFreeUnderAge !== undefined) validateAdditionalPassengerFreeUnderAge();
    };

    const addVehicleBaseFareConfig = () => {
        state.vehicleBaseFareConfigs.push({ vehicleType: '', baseFare: 0.00 });
    };

    const removeVehicleBaseFareConfig = (index) => {
        state.vehicleBaseFareConfigs.splice(index, 1);
    };

    const addDistanceFareConfig = () => {
        state.distanceFareConfigs.push({
            startDistanceMeters: 0,
            endDistanceMeters: 0,
            isStartingRange: false,
            isOpenEnded: false,
            rateType: '',
            rateAmount: 0.00
        });
    };

    const removeDistanceFareConfig = (index) => {
        state.distanceFareConfigs.splice(index, 1);
    };

    const addGeoZoneFareConfig = () => {
        state.geoZoneFareConfigs.push({
            geoZone: null,
            applicationType: '',
            zoneRateType: 'SURCHARGE',
            surcharge: 0.00,
            percentage: 0.00,
            flatFare: 0.00,
            zonePerKmRate: 0.00,
            fareMaximum: null,
            fareMaximumUpToMeters: null,
            fareMinimum: null,
            fareMinimumUpToMeters: null,
            zonePlatformFee: null,
            zonePlatformFeePerKm: null,
        });
    };

    const removeGeoZoneFareConfig = (index) => {
        state.geoZoneFareConfigs.splice(index, 1);
    };

    setTimeout(initializeValidation, 100);

    return {
        state,
        validations,
        isValid,
        initializeValidation,
        addVehicleBaseFareConfig,
        removeVehicleBaseFareConfig,
        validateVehicleBaseFareConfig,
        checkDuplicateVehicleType,
        addDistanceFareConfig,
        removeDistanceFareConfig,
        validateDistancePricing,
        checkOverlappingDistanceFareConfig,
        addGeoZoneFareConfig,
        removeGeoZoneFareConfig,
        validateGeoZone,
        checkDuplicateGeoZone,
        validateFreeStopWaitingMinutes,
        validateStopPerMinuteCharge,
        validatePerStopCharge,
        validatePlatformFee,
        isPlatformFeePerKmBelowFloor,
        validatePlatformFeePerKm,
        validatePlatformFeeMinimum,
        validatePlatformFeeMaximum,
        validateScheduledRideFee,
        validatePerMinuteFare,
        validateMinimumFare,
        validatePerAdditionalPassengerCharge,
        validateAdditionalPassengerMaxCharge,
        validateAdditionalPassengerFreeUnderAge,
    };
}
